<template>
  <section>
    <div v-if="publications.data.length == 0">
      Lo sentimos. No se encontraron publicaciones.
    </div>
    <div v-else>
      <ButtonsGroup>
        <Button
          tag="button"
          @click="changeView(DEFAULT_VIEW)"
          size="small"
          :active="view == DEFAULT_VIEW"
        >
          <List />
        </Button>
        <Button
          tag="button"
          @click="changeView(APA_VIEW)"
          size="small"
          :active="view != DEFAULT_VIEW"
        >
          <Article />
        </Button>
      </ButtonsGroup>
      <template v-for="(data, pubIndex) in publications.data" :key="pubIndex">
        <h2 v-if="data.items.length > 0">
          {{ data.year }} <small>({{ data.items.length }})</small>
        </h2>
        <template
          v-for="(publication, itemIndex) in data.items"
          :key="itemIndex"
        >
          <SimplePublication
            v-if="view != DEFAULT_VIEW"
            :orderNumber="pubIndex + 1 + itemIndex"
            :title="publication.title"
            :authors="publication.authors"
            :dateIssued="publication.dateIssued"
            :publisher="publication.publisher"
            :abstract="publication.abstract"
            :sourcePath="publication.url"
            :thumbnailPath="publication.thumbnail"
            :type="publication.type"
            :degree="publication.degree"
            :grantor="publication.grantor"
          />
          <APAPublication
            v-else
            :orderNumber="pubIndex + 1 + itemIndex"
            :title="publication.title"
            :authors="publication.authors"
            :dateIssued="publication.dateIssued"
            :publisher="publication.publisher"
            :journal="publication.journal"
            :abstract="publication.abstract"
            :sourcePath="publication.url"
            :thumbnailPath="publication.thumbnail"
            :type="publication.type"
            :doi="publication.doi"
            :degree="publication.degree"
            :grantor="publication.grantor"
          />
        </template>
        <br v-if="data.items.length > 0" />
      </template>
      <Pagination
        v-if="paginated && hasMorePages"
        :total="total"
        :currentPage="currentPage"
        :itemsPerPage="itemsPerPage"
        @updateItemsPerPage="updateItemsPerPage"
        @updateCurrentPage="updateCurrentPage"
      />
      <div style="width: 100%; display: grid; place-items: center;">
        <Button
          v-if="!paginated && hasMorePages"
          tag="button"
          @click="loadMore()"
          :active="isLoading"
          style="min-width:300px;"
          size="large"
        >
          <template v-if="isLoading">
            <Spinner style="margin-right: 1ch;" />
            Cargando...
          </template>
          <template v-else>
            Cargar más publicaciones
          </template>
        </Button>
      </div>
    </div>
  </section>
</template>
<script>
import { getPublicationsByCollection } from "@/api/publications.js";
import Pagination from "@/components/ui/objects/Pagination";
import { reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import APAPublication from "@/components/ui/objects/publication/Formated";
import SimplePublication from "@/components/ui/objects/publication/Default";
import Button from "@/components/ui/atoms/Button";
import ButtonsGroup from "@/components/ui/objects/ButtonsGroup";
import Article from "@/components/ui/icons/Article";
import List from "@/components/ui/icons/List";
import Spinner from "@/components/ui/icons/Spinner";
export default {
  components: {
    SimplePublication,
    Pagination,
    APAPublication,
    Button,
    Article,
    List,
    ButtonsGroup,
    Spinner,
  },
  props: {
    documentType: {
      type: String,
    },
    documentSubtype: {
      type: String,
    },
    url: {
      type: String,
    },
    collectionId: {
      type: String,
    },
    paginated: {
      type: Boolean,
      default: false,
    },
    style: {
      type: String,
      default: "default",
      validator(value) {
        return ["APA", "default"].includes(value);
      },
    },
  },
  async setup(props) {
    const router = useRouter();
    const route = useRoute();

    const APA_VIEW = "APA";
    const DEFAULT_VIEW = "default";
    const DEFALUT_ITEMS_PER_PAGE = 20;
    //temp
    let view = ref(props.style);
    const changeView = (newView) => {
      view.value = newView;
    };

    const countParam = route.query.count;
    let currentPage = parseInt(route.query.page) || 1;
    const itemsPerPage = parseInt(countParam) || DEFALUT_ITEMS_PER_PAGE;

    let total = 0;
    let publications = reactive({ data: [] });
    let options = reactive({
      url: props.url,
      collection_id: props.collectionId,
      currentPage,
      itemsPerPage,
    });

    const getMetadata = (item, metadata_field_id) => {
      const search = item.metadata.find(
        (md) => md.metadata_field_id == metadata_field_id
      );
      return (search && search.value) || "";
    };

    const getMetadataArray = (item, metadata_field_id) => {
      const search = item.metadata.filter(
        (md) => md.metadata_field_id == metadata_field_id
      );
      return (search && search.map((md) => getAPAformat(md.value))) || [];
    };

    const getAPAformat = (author) => {
      const fullName = author.split(",");
      const surname = fullName[0];
      let authorName = surname;
      if (fullName.length == 2) {
        authorName += `, ${fullName[1].substring(0, 2)}.`;
      }
      return authorName;
    };

    const getCitation = (item) => {
      return getMetadata(item, 18);
    };

    const getYear = (date) => {
      return date.substring(0, 4);
    };

    const getTitle = (item) => {
      return getMetadata(item, 64);
    };

    const getDateIssued = (item) => {
      return getYear(getMetadata(item, 15));
    };

    const getPublisher = (item) => {
      return getMetadata(item, 39);
    };

    const getAbstract = (item) => {
      return getMetadata(item, 27);
    };

    const getAuthors = (item) => {
      return getMetadataArray(item, 3);
    };

    const getUrl = (item) => {
      return getMetadata(item, 25);
    };

    const getDOI = (item) => {
      return getMetadata(item, 141);
    };

    const getJournal = (item) => {
      return getMetadata(item, 139);
    };

    const getType = (item) => {
      return getMetadata(item, 66);
    };

    const getDegree = (item) => {
      return getMetadata(item, 134);
    };

    const getGrantor = (item) => {
      return getMetadata(item, 39);
    };

    let hasMorePages = ref(false);
    let itemsByYear = reactive([]);

    const formatMetadata = (items, itemsByYear, item) => {
      const number = getCitation(item) || "No citation";
      const volumen = getCitation(item) || "No citation";
      const title = getTitle(item) || "No title";
      const authors = getAuthors(item) || "No authors";
      const dateIssued = getDateIssued(item) || "No date issued";
      const publisher = getPublisher(item) || "No publisher";
      const abstract = getAbstract(item) || "No Abstract";
      const url = getUrl(item) || "No url";
      const thumbnail = item.thumbnail;
      const doi = getDOI(item);
      const journal = getJournal(item);
      const type = getType(item);
      const degree = getDegree(item);
      const grantor = getGrantor(item);
      items = itemsByYear.find((i) => i.year == dateIssued).items;
      items.push({
        title,
        authors,
        dateIssued,
        publisher,
        abstract,
        url,
        thumbnail,
        doi,
        journal,
        number,
        volumen,
        type,
        degree,
        grantor,
      });
      return items;
    };

    await getPublicationsByCollection(options)
      .then((response) => {
        let items = [];
        const results = response.items.data;
        const years = response.facetIssuedYear;
        years.forEach((year) => {
          itemsByYear.push({
            year,
            items: [],
          });
        });
        results.forEach((item) => {
          formatMetadata(items, itemsByYear, item);
        });
        publications.data = itemsByYear;
        total = response.items.paginatorInfo.total;
        hasMorePages.value = response.items.paginatorInfo.hasMorePages;
      })
      .catch(() => {
        console.log("No publications found...");
      });

    let isLoading = ref(false);
    const loadMore = async () => {
      currentPage += 1;
      options = {
        url: props.url,
        collection_id: props.collectionId,
        currentPage,
        itemsPerPage,
      };
      isLoading.value = true;
      await getPublicationsByCollection(options).then((response) => {
        response.items.data.forEach((item) => {
          let items = [];
          formatMetadata(items, itemsByYear, item);
        });
        isLoading.value = false;
        hasMorePages.value = response.items.paginatorInfo.hasMorePages;
      });
    };

    const updateItemsPerPage = (newItemsPerPage) => {
      router.push({
        params: route.params,
        query: { ...route.query, count: newItemsPerPage, page: 1 },
      });
    };

    const updateCurrentPage = (newCurrentPage) => {
      router.push({
        params: route.params,
        query: { ...route.query, page: newCurrentPage },
      });
    };

    return {
      publications,
      total,
      itemsPerPage,
      currentPage,
      updateItemsPerPage,
      updateCurrentPage,
      countParam,
      view,
      changeView,
      APA_VIEW,
      DEFAULT_VIEW,
      loadMore,
      isLoading,
      hasMorePages,
    };
  },
};
</script>
